import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { useParams } from "@remix-run/react";
import * as Sentry from "@sentry/react";

import { supabase } from "~/integrations/supabase/ssr.browser";
import { getCachedEligibleTriggers } from "~/features/surveys/utils/eligible-triggers-cache";

import { ENABLE_ANALYTICS } from "./constants";
import { getBrowserEnvVar, IS_TEST_MODE } from "./env.shared";
import type { AnalyticsEvent } from "./events";
import { isInStandaloneMode } from "./ios-install";
import { logger } from "./logger";
import { setSentryUser, setupSentry } from "../lib/sentry/client";

// Hook to get sessionId from URL params
export const useSessionIdFromUrl = () => {
    const params = useParams();
    return params.sessionId;
};

/* Simple heuristic to determine if the user is a recipient or owner based on the URL 
	This breaks when the client is opening their own sharing link, but this is hopefully 
	rare an edge case.
*/

export const getOwnershipFromPath = (pathname: string): string | undefined => {
    const isSharingLink = pathname.includes("/sharing");
    return isSharingLink ? "recipient" : "owner";
};

// Function to extract sessionId from pathname
export const getSessionIdFromPath = (pathname: string): string | undefined => {
    const sessionIdMatch = pathname.match(/\/sessions\/([^/]+)/);
    return sessionIdMatch ? sessionIdMatch[1] : undefined;
};

// ----------------------------------------------
// Customer.io (browser) – initialize once
// ----------------------------------------------

let customerIo: AnalyticsBrowser | null = null;
let lastCustomerIoPayload: Record<string, unknown> | null = null;

// AIDEV-NOTE: Track if analytics has been initialized to prevent duplicate initialization
let analyticsInitialized = false;

// AIDEV-NOTE: Current userType cached for enriching events and late identifies

// AIDEV-NOTE: Unused equality helper removed to reduce complexity

/**
 * Identify (create/update) a user in Customer.io.
 *
 * We intentionally run this in the browser because the server does not have access
 * to the end-user's auth state. The request is authenticated via Basic auth where
 * the username is the API key and the password is left blank. We therefore send
 * the header `Authorization: Basic ${btoa(`${apiKey}:`)}` as per Customer.io docs.
 */
const identifyCustomerIoUser = async (
    user: {
        id: string;
        email?: string;
        user_metadata?: { name?: string };
    } | null,
    extraTraits?: Record<string, unknown>,
) => {
    if (!user || !customerIo) return;

    const traits: Record<string, unknown> = {};
    if (user.email) traits.email = user.email;
    if (user.user_metadata?.name) traits.name = user.user_metadata.name;
    // AIDEV-NOTE: Always include environment for filtering in Customer.io
    traits.environment = getBrowserEnvVar("ENV_NAME") || "unknown";
    if (extraTraits) Object.assign(traits, extraTraits);

    const identifyPayload = {
        userId: user.id,
        traits,
    };

    if (
        JSON.stringify(identifyPayload) ===
        JSON.stringify(lastCustomerIoPayload)
    ) {
        return;
    }

    try {
        // AIDEV-NOTE: Use email as primary identifier for auth'd users (email→email identifier in CIO)
        // Use Supabase ID for anonymous users (userId→id identifier). Prevents "Invalid Email" segment.
        if (user.email) {
            await customerIo.identify(user.email, {
                ...traits,
                supabase_user_id: user.id,
            });
        } else {
            await customerIo.identify(user.id, traits);
        }

        lastCustomerIoPayload = { ...identifyPayload };

        logger
            .withMetadata({
                identifier: user.email || user.id,
                supabaseUserId: user.id,
                hasEmail: !!user.email,
            })
            .debug("Successfully identified user in Customer.io (browser)");
    } catch (error) {
        const errorMessage =
            error instanceof Error ? error.message : String(error);

        // Silently skip "already associated" errors from redundant identifies
        if (
            errorMessage.includes("already associated with a different profile")
        ) {
            logger
                .withMetadata({ userId: user.id, email: user.email })
                .debug("Customer.io identify skipped - profile already exists");
            return;
        }

        captureException("Failed to identify user in Customer.io", error);
    }
};

// ---------------------------
// Identity helpers (modularized)
// ---------------------------

export type UserType = "client" | "practitioner" | "consultant" | "unknown";
let currentUserType: UserType | undefined = undefined;

export const resetCustomerIoIdentity = async (): Promise<void> => {
    lastCustomerIoPayload = null;
    currentUserType = undefined;

    if (!customerIo) {
        return;
    }

    try {
        await customerIo.reset();
    } catch (error) {
        captureException("Failed to reset Customer.io identity", error);
    }
};

type AnalyticsUser =
    | {
          id: string;
          email?: string;
          user_metadata?: { name?: string };
          is_anonymous?: boolean;
      }
    | null
    | undefined;

// AIDEV-NOTE: Applies logger + Sentry identity, respecting consent for PII in logs
const applyLoggerAndSentry = (user: AnalyticsUser, hasConsent: boolean) => {
    // Set user context in Sentry — id only (no email; PHI must not reach Sentry)
    setSentryUser(user?.id || null);

    // Add context to logger only if consent; otherwise anonymous flag only.
    // AIDEV-NOTE: HIPAA — never attach userEmail to the request-scoped logger; it
    // propagated a direct identifier onto every server log (Jun-2026 Datadog sweep).
    // Use userId only — email is recoverable from the id when genuinely needed.
    if (hasConsent && user) {
        logger.withContext({ userId: user.id });
    } else {
        logger.withContext({ isAuthenticated: !!user });
    }
};

// AIDEV-NOTE: Updates Customer.io profile; userType is essential account data (no consent required)
const applyCustomerIo = (
    user: AnalyticsUser,
    userType: UserType | undefined,
) => {
    if (!user || user.is_anonymous) return;
    // AIDEV-NOTE: userType is essential for account management, always send it
    const traits = userType ? { userType } : undefined;
    if (userType) currentUserType = userType;
    identifyCustomerIoUser(user, traits);
};

// AIDEV-NOTE: Single entry to update all identities consistently
const applyIdentities = (
    user: AnalyticsUser,
    userType: UserType | undefined,
    hasConsent: boolean,
) => {
    applyLoggerAndSentry(user, hasConsent);
    applyCustomerIo(user, userType);
};

// ---------------------------
// Customer.io SDK helper
// ---------------------------

const sendCustomerIoEvent = async (
    event: string,
    properties: Record<string, unknown>,
    explicitUserId?: string | null,
) => {
    if (!customerIo) {
        return;
    }

    try {
        // AIDEV-NOTE: Only include userId in properties if explicitly provided
        // Otherwise, CIO SDK uses the last identified user automatically
        const eventProperties = explicitUserId
            ? { userId: explicitUserId, ...properties }
            : properties;

        await customerIo.track(event, eventProperties);
    } catch (error) {
        captureException("Failed to send Customer.io event", error);
    }
};

export const initAnalytics = (user: AnalyticsUser) => {
    // AIDEV-NOTE: Prevent duplicate initialization in StrictMode or multiple renders
    if (analyticsInitialized) {
        // Still update user context if a user is provided
        applyIdentities(user, currentUserType, getHasAnalyticsConsent());
        return;
    }

    if (!ENABLE_ANALYTICS) {
        return;
    }

    // Mark as initialized early to prevent race conditions
    analyticsInitialized = true;

    const customerIoWriteKey = getBrowserEnvVar("CUSTOMERIO_API_KEY");
    // AIDEV-NOTE: Customer.io sends to production from all environments with environment tags
    customerIo = customerIoWriteKey
        ? AnalyticsBrowser.load({ writeKey: customerIoWriteKey })
        : null;

    if (!customerIo) {
        captureException(
            "Customer.io not initialized",
            new Error("Customer.io not initialized"),
        );
    }

    try {
        // AIDEV-NOTE: Skip Sentry and Datadog in test mode to avoid noise
        if (!IS_TEST_MODE) {
            // Initialize Sentry (error tracking is legitimate interest - no PII)
            setupSentry();

            const datadogClientToken = getBrowserEnvVar("DATADOG_CLIENT_TOKEN");
            // Initialize Datadog - only for essential error logging without consent
            if (datadogClientToken) {
                datadogLogs.init({
                    clientToken: datadogClientToken,
                    forwardErrorsToLogs: true,
                    site: "datadoghq.com",
                    service: "frontend",
                    env: getBrowserEnvVar("ENV_NAME") || "unknown",
                });
            }
        }

        // AIDEV-NOTE: Handle initial user if provided from AnalyticsProvider
        applyIdentities(user, currentUserType, getHasAnalyticsConsent());

        supabase.auth.onAuthStateChange((event, session) => {
            if (event !== "INITIAL_SESSION" && event !== "SIGNED_IN") {
                logger
                    .withMetadata({ event, hasUser: !!session?.user })
                    .debug("[Auth] state change:");
            }
            applyIdentities(
                session?.user,
                currentUserType,
                getHasAnalyticsConsent(),
            );
        });

        logger.info("Analytics initialized successfully");
    } catch (error) {
        logger
            .withError(error as Error)
            .error("Failed to initialize analytics");
    }
};

const getEnrichedProps = () => {
    const sessionIdFromUrl = getSessionIdFromPath(window.location.pathname);
    const ownership = getOwnershipFromPath(window.location.pathname);

    // AIDEV-NOTE: Environment detection for analytics - uses VITE_APP_ENV_NAME
    const environment = getBrowserEnvVar("ENV_NAME") || "unknown";

    // AIDEV-NOTE: Surface detection — "pwa" when launched from an installed Home Screen /
    // standalone app, "browser" otherwise. Added to every event so we can segment installed-app
    // usage. Re-read per event (not cached) since display-mode can change within a tab session.
    const displayMode = isInStandaloneMode() ? "pwa" : "browser";

    return {
        ...(sessionIdFromUrl && { session: sessionIdFromUrl }),
        ...(ownership && { ownership }),
        ...(currentUserType ? { userType: currentUserType } : {}),
        environment,
        displayMode,
    };
};

// ---------------------------
// Survey trigger checking helper (client-side)
// ---------------------------

// AIDEV-NOTE: Check if this event should trigger a survey (non-blocking, fire-and-forget)
// First checks local cache of eligible triggers to skip POST for non-matching events
const checkSurveyTriggersClient = async (eventName: string) => {
    try {
        // AIDEV-NOTE: Check local cache first to skip unnecessary POST requests
        // Cache is populated by SurveyProvider from prefetched data in root.tsx
        const eligibleTriggers = getCachedEligibleTriggers();
        if (eligibleTriggers && !eligibleTriggers.has(eventName)) {
            // Event doesn't match any survey trigger - skip POST
            return;
        }

        // Only check for authenticated users
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        // Event matches an eligible trigger (or cache not ready) - POST to queue endpoint
        const API_URL = window.location.origin;
        await fetch(`${API_URL}/api/surveys/queue-survey`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ eventName }),
        });

        // AIDEV-NOTE: Dispatch event to notify SurveyProvider to refetch active surveys
        // This ensures newly queued surveys appear without requiring page refresh
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("survey-trigger-checked"));
        }
    } catch {
        // AIDEV-NOTE: Silently fail - survey triggers shouldn't block analytics
    }
};

// 2. Define the trackEvent function so that TypeScript will infer the
//    expected props from the event name.
// 2. Define the trackEvent function so that TypeScript will infer the
//    expected props from the event name.
export function trackEvent<E extends AnalyticsEvent["name"]>(
    name: E,
    props: Extract<AnalyticsEvent, { name: E }>["props"],
): void {
    try {
        const extraProps = getEnrichedProps();

        const enrichedProps = {
            ...props,
            ...extraProps,
        };
        logger.withMetadata(enrichedProps).debug(`[Analytics]: ${name}`);

        // AIDEV-NOTE: Determine if this is an essential event for service functionality
        // Essential events are those required for transactional emails and service operation
        const essentialEvents = [
            "signed up",
            "email confirmed", // AIDEV-NOTE: EVO-2216 — lifecycle event that triggers the practitioner activation automation; must reach Customer.io regardless of analytics consent (same legitimate-interest basis as "signed up"/"terms accepted")
            "logged in",
            "scheduled reminder",
            "cookie consent updated", // Legal requirement - always track consent interactions
            "terms accepted", // Essential for account onboarding and transactional communications
        ];

        // Customer.io receives essential events for transactional communications (GDPR legitimate interest)
        // OR all events if user has given analytics consent
        if (getHasAnalyticsConsent() || essentialEvents.includes(name)) {
            sendCustomerIoEvent(name, enrichedProps);
        }

        // AIDEV-NOTE: Check if this event should trigger a survey (non-blocking, client-side)
        // Fire-and-forget - don't await to avoid blocking analytics
        checkSurveyTriggersClient(name).catch(() => {
            // Already handled in checkSurveyTriggersClient, but catch to prevent unhandled promise rejection
        });
    } catch (error) {
        if (error instanceof Error) {
            if (error.name === "TypeError") {
                logger.info("Event not sent because analytics are disabled.");
            } else {
                captureException(`Failed to track event: ${name}`, error);
            }
        } else {
            captureException(
                `Failed to track event: ${name}`,
                new Error(String(error)),
            );
        }
    }
}

export function trackEventAsUser<E extends AnalyticsEvent["name"]>(
    name: E,
    sessionUserId: string,
    props: Extract<AnalyticsEvent, { name: E }>["props"],
    apiClient?: {
        post: (url: string, data: Record<string, unknown>) => Promise<Response>;
    },
) {
    const extraProps = getEnrichedProps();

    const enrichedProps = {
        ...props,
        ...extraProps,
    };

    logger.withMetadata(enrichedProps).debug(`[Analytics]: ${name}`);

    if (apiClient) {
        // AIDEV-NOTE: Use provided apiClient when available
        return apiClient
            .post("/analytics/track-as-user", {
                event: name,
                eventUserId: sessionUserId,
                props: enrichedProps,
            })
            .catch((error) => {
                logger.error(`[Analytics] Failed to track event: ${error}`);
                captureException("Failed to track analytics event", error);
            });
    } else {
        // AIDEV-NOTE: Fallback to fetch for backwards compatibility
        // This will be removed once all callers are updated to pass apiClient
        const API_URL = ""; // This will cause an error, encouraging migration
        return fetch(API_URL + "/analytics/track-as-user", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                event: name,
                eventUserId: sessionUserId,
                props: enrichedProps,
            }),
        }).catch((error) => {
            logger.error(`[Analytics] Failed to track event: ${error}`);
            captureException("Failed to track analytics event", error);
        });
    }
}

// AIDEV-NOTE: `context` (and optional extras) now reach Sentry as extras —
// previously only the bare error was sent, so client failures landed as
// anonymous "Failed to fetch" events with no clue which flow threw them.
// Extras must stay PHI-free (IDs/sizes/codes only).
export const captureException = (
    context: string,
    error: unknown,
    extras?: Record<string, unknown>,
) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    logger.withError(errorObj).error(`${context}: ${errorObj.message}`);
    return Sentry.captureException(errorObj, {
        extra: { context, ...extras },
    });
};

export const trackViewedModal = (
    modalName: string,
    props: Record<string, unknown> = {},
) => {
    trackEvent("viewed modal", { modal: modalName, ...props });
};

// Safari Private Browsing (and some strict mobile settings) throw a synchronous
// QuotaExceededError the first time localStorage.setItem is called. We reuse the
// same feature-detection logic we added in the Supabase client shim so that all
// analytics code degrades gracefully when persistent storage is blocked.
const isLocalStorageWritable = (() => {
    if (typeof window === "undefined") return false;
    try {
        const testKey = "__storage_test__";
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
})();

const safeGet = (key: string): string | null => {
    if (!isLocalStorageWritable) return null;
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};
const safeSet = (key: string, value: string) => {
    if (!isLocalStorageWritable) return;
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* ignore */
    }
};

// AIDEV-NOTE: Legal requirement - default to opt-out (false) unless user explicitly consented
export const getHasAnalyticsConsent = () => {
    const saved = safeGet("hasConsentedToAnalytics");
    return saved === "true"; // false by default, true only if explicitly consented
};

export const setAnalyticsConsent = async (
    consent: boolean,
    user?: {
        id: string;
        email?: string;
    } | null,
) => {
    try {
        // AIDEV-NOTE: Initialize Customer.io if not already initialized
        if (!customerIo) {
            const customerIoWriteKey = getBrowserEnvVar("CUSTOMERIO_API_KEY");
            if (customerIoWriteKey) {
                customerIo = AnalyticsBrowser.load({
                    writeKey: customerIoWriteKey,
                });
                logger.info("Customer.io initialized in setAnalyticsConsent");
            }
        }

        safeSet("hasConsentedToAnalytics", consent.toString());

        // AIDEV-NOTE: Identify user BEFORE tracking consent event so CIO knows which profile to attach event to
        if (consent) {
            if (user) {
                logger
                    .withMetadata({ userId: user.id, email: user.email })
                    .info(
                        "Identifying user before tracking consent accepted event",
                    );
                initAnalytics(user);
            }
        } else {
            // AIDEV-NOTE: Even if declining, identify the user in CIO so consent event is tracked
            if (user) {
                logger
                    .withMetadata({ userId: user.id, email: user.email })
                    .info(
                        "Identifying user before tracking consent declined event",
                    );
                await identifyCustomerIoUser(user);
            }
        }

        // AIDEV-NOTE: Emit explicit consent event for product analytics (after user is identified)
        trackEvent("cookie consent updated", {
            status: consent ? "accepted" : "declined",
        });

        // AIDEV-NOTE: Screen tracking handled by AnalyticsScreenTracker component
        // No need to manually track here
    } catch (error) {
        logger
            .withError(error as Error)
            .error("Failed to set analytics consent");
    }
};

export const hasUserMadeConsentChoice = () => {
    if (typeof window === "undefined") return false;
    const saved = safeGet("hasConsentedToAnalytics");
    return saved === "true" || saved === "false";
};
